import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import Icon from '@/components/Icon';
import { useCoworkerStore } from '@/store/coworker';
import { useChatInputStore, useChatStore } from '@/store';
import useArtifactEditConversation from '@/hooks/useArtifactEditConversation';
import { filesApi } from '@/lib/api/files';
import { coworkerApi } from '@/lib/api/coworker';
import { downloadMedia } from '@/constants/utils/mediaDownload';
import { conversationImageOutputs, imageEditPrompt, imageFileId, imageFileKey, imageFileUrl, taskImageOutputs } from '@/lib/imageArtifact';

const TOOLS = [
  ['edit', 'wand-magic-sparkles', 'Edit'],
  ['comment', 'comment', 'Comment'],
  ['background', 'image', 'Remove BG'],
  ['remove', 'eraser', 'Remove'],
  ['resize', 'expand', 'Resize'],
  ['variation', 'copy', 'Variation'],
];
const TOOL_HELP = {
  edit: 'Describe a change. Select an area to focus the edit, or edit the whole image.',
  comment: 'Select an area and describe what should change there.',
  background: 'Keep the subject and remove its background. The result will be a transparent PNG.',
  remove: 'Drag around the object to remove. AI will fill the space to match its surroundings.',
  resize: 'Choose a new shape. AI will reframe the image without stretching the subject.',
  variation: 'Create a fresh version of this image, keeping its subject and visual style.',
  generate: 'Describe a new image from scratch. No source image will be used.',
};
const buttonStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, borderWidth: 1, borderStyle: 'solid', borderColor: '#383838', borderRadius: 9, padding: '9px 11px', background: '#252525', color: '#eee', font: 'inherit', cursor: 'pointer' };

function Button({ children, active, ...props }) {
  return <button type="button" {...props} style={{ ...buttonStyle, ...(active ? { background: '#344a3c', borderColor: '#77b98d' } : {}), ...(props.disabled ? { opacity: 0.45, cursor: 'default' } : {}), ...props.style }}>{children}</button>;
}

export default function ImageEditorPanel({ file, onClose }) {
  const editConversation = useArtifactEditConversation();
  const events = useCoworkerStore(state => state.transcriptEventsByTask);
  const chats = useChatStore(state => state.chats);
  const outputs = useMemo(() => {
    const images = new Map();
    const candidates = [file, ...conversationImageOutputs(chats, file), ...taskImageOutputs(events, file)].filter(Boolean);
    const persistedPaths = new Map(candidates.filter(item => imageFileId(item) && item.taskId && item.path).map(item => [`${item.taskId}:${item.path}`, imageFileId(item)]));
    for (const item of candidates) {
      const key = imageFileId(item) || persistedPaths.get(`${item.taskId}:${item.path}`) || imageFileKey(item);
      if (key) images.set(key, { ...images.get(key), ...item });
    }
    return [...images.values()];
  }, [events, chats, file]);
  const [selection, setSelected] = useState(file);
  const [uploads, setUploads] = useState([]);
  const [tool, setTool] = useState('edit');
  const [instruction, setInstruction] = useState('');
  const [region, setRegion] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [size, setSize] = useState('1:1');
  const [imageDimensions, setImageDimensions] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('Preparing…');
  const [resolvedImages, setResolvedImages] = useState({});
  const [loadErrors, setLoadErrors] = useState({});
  const [preparedKey, setPreparedKey] = useState('');
  const [preparedPrompt, setPreparedPrompt] = useState('');
  const [imageError, setImageError] = useState(false);
  const [viewport, setViewport] = useState({ width: 600, height: 500 });
  const canvasRef = useRef(null);
  const uploadRef = useRef(null);
  const dragRef = useRef(null);
  const aliveRef = useRef(true);
  const operationRef = useRef(0);
  const resolutionRef = useRef(new Map());
  const attachmentRef = useRef(new Map());
  const instructionRef = useRef(null);
  const selectedOutput = outputs.find(item => imageFileKey(item) === imageFileKey(selection)
    || (!imageFileId(selection) && item.taskId && item.taskId === selection?.taskId && item.path && item.path === selection?.path));
  const selected = { ...selection, ...selectedOutput, ...resolvedImages[imageFileKey(selectedOutput || selection)] };
  const url = imageFileUrl(selected);
  const dimensions = imageDimensions?.url === url ? imageDimensions : null;
  // Cached images may finish before React attaches the load handler. Read them
  // at attachment too, and keep measurements tied to the selected source URL.
  const measureImage = useCallback(element => {
    if (element?.complete && element.naturalWidth > 0 && element.naturalHeight > 0) {
      setImageDimensions({ url, width: element.naturalWidth, height: element.naturalHeight });
    }
  }, [url]);
  const allImages = [...outputs, ...uploads.filter(item => !outputs.some(output => imageFileKey(output) === imageFileKey(item)))]
    .map(item => ({ ...item, ...resolvedImages[imageFileKey(item)] }))
    .filter((item, index, images) => !images.slice(0, index).some(previous => imageFileKey(previous) === imageFileKey(item)
      || (imageFileUrl(item) && imageFileUrl(previous) === imageFileUrl(item))));
  const generating = tool === 'generate';
  const requestKey = JSON.stringify([imageFileKey(selected), tool, instruction.trim(), region, size]);
  const composerText = useSyncExternalStore(useChatInputStore.subscribe, () => {
    const composer = useChatInputStore.getState();
    return composer.inputStates[composer.currentContextId]?.text || '';
  }, () => '');
  const alreadyPrepared = preparedKey === requestKey && !!preparedPrompt && composerText.includes(preparedPrompt);
  const needsInstruction = ['edit', 'comment', 'generate'].includes(tool);
  const requestReady = (!needsInstruction || !!instruction.trim()) && (tool !== 'remove' || !!region);

  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; operationRef.current += 1; }; }, []);
  useEffect(() => {
    operationRef.current += 1;
    setSelected(file); setUploads([]); setInstruction(''); setNotice(''); setError(''); setBusy(false); setTool('edit'); setPreparedKey('');
  }, [file]);
  useEffect(() => { setRegion(null); setZoom(1); setImageError(false); dragRef.current = null; }, [url]);
  useEffect(() => {
    const element = canvasRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setViewport({ width: entry.contentRect.width, height: entry.contentRect.height }));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const resolveImage = item => {
    if (imageFileUrl(item)) return Promise.resolve(item);
    const key = imageFileKey(item);
    if (!resolutionRef.current.has(key)) {
      setLoadErrors(errors => ({ ...errors, [key]: '' }));
      const pending = (async () => {
        const resolved = imageFileId(item)
          ? { ...item, ...await filesApi.fetchFile(imageFileId(item)) }
          : { ...item, url: await coworkerApi.getArtifactDownloadUrl(item.taskId, item.path) };
        if (!imageFileUrl(resolved)) throw new Error('This image is not available yet. Please try again.');
        if (aliveRef.current) setResolvedImages(items => ({ ...items, [key]: resolved }));
        return resolved;
      })().catch(err => {
        resolutionRef.current.delete(key);
        if (aliveRef.current) setLoadErrors(errors => ({ ...errors, [key]: err.message || 'Could not load this image.' }));
        throw err;
      });
      resolutionRef.current.set(key, pending);
    }
    return resolutionRef.current.get(key);
  };
  useEffect(() => {
    let cancelled = false;
    const missing = outputs.filter(item => !imageFileUrl(item));
    const worker = async () => {
      while (!cancelled && missing.length) {
        const item = missing.shift();
        try { await resolveImage(item); } catch { /* Clicking the thumbnail offers a retry. */ }
      }
    };
    Promise.all(Array.from({ length: Math.min(3, missing.length) }, worker));
    return () => { cancelled = true; };
  }, [outputs]);

  const changeTool = next => {
    if (next === tool) { instructionRef.current?.focus(); return; }
    setTool(next); setNotice(''); setError('');
    if (!['edit', 'comment', 'remove'].includes(next)) setRegion(null);
    if (next === 'generate' || generating) setInstruction('');
    instructionRef.current?.focus();
  };

  const select = async item => {
    const op = ++operationRef.current;
    setError(''); setNotice(''); setBusyLabel('Loading image…'); setBusy(true);
    try {
      const resolved = await resolveImage(item);
      if (aliveRef.current && operationRef.current === op) {
        setSelected(resolved);
        if (generating) { setTool('edit'); setInstruction(''); }
      }
    } catch (err) {
      if (aliveRef.current && operationRef.current === op) setError(err.message || 'Could not load this image.');
    } finally { if (aliveRef.current && operationRef.current === op) setBusy(false); }
  };

  const upload = async event => {
    const source = event.target.files?.[0];
    event.target.value = '';
    if (!source) return;
    if (!source.type.startsWith('image/')) { setError('Choose an image file, such as PNG, JPEG, or WebP.'); return; }
    if (source.size > 50 * 1024 * 1024) { setError('Choose an image smaller than 50 MB.'); return; }
    const op = ++operationRef.current;
    setBusyLabel('Uploading image…'); setBusy(true); setError(''); setNotice('');
    try {
      const result = await filesApi.uploadFile({ file: source, name: source.name });
      if (aliveRef.current && operationRef.current === op) {
        if (!result.file || !imageFileUrl(result.file)) throw new Error('The upload did not return an image. Please try again.');
        setUploads(items => [...items.filter(item => imageFileKey(item) !== imageFileKey(result.file)), result.file]);
        setSelected(result.file); setTool('edit'); setInstruction(''); setPreparedKey('');
      }
    } catch (err) { if (aliveRef.current && operationRef.current === op) setError(err.message || 'Upload failed. Please try again.'); }
    finally { if (aliveRef.current && operationRef.current === op) setBusy(false); }
  };

  const prepareEdit = async () => {
    if (busy || alreadyPrepared || !requestReady) return;
    const op = ++operationRef.current;
    setBusyLabel('Preparing…'); setBusy(true); setError(''); setNotice('');
    try {
      const target = editConversation.captureTarget();
      let source = attachmentRef.current.get(imageFileKey(selected)) || selected;
      if (!generating && !imageFileId(source)) {
        if (!source?.taskId || !source?.path) throw new Error('Upload this image first to use it as an AI editing source.');
        const response = await coworkerApi.fetchArtifactBinary(source.taskId, source.path, { asProxy: true });
        if (!response.ok) throw new Error('Could not attach the source image. Please try again.');
        const blob = await response.blob();
        if (!aliveRef.current || operationRef.current !== op) return;
        editConversation.assertTarget(target);
        const uploaded = await filesApi.uploadFile({ file: new File([blob], source.name || 'image.png', { type: blob.type }), name: source.name });
        if (!imageFileId(uploaded.file)) throw new Error('Could not attach the source image. Please try again.');
        source = { ...source, ...uploaded.file };
      }
      if (!generating && (!source.name || !source.cdnPath)) source = { ...source, ...await filesApi.fetchFile(imageFileId(source)) };
      if (!aliveRef.current || operationRef.current !== op) return;
      const prompt = imageEditPrompt({ tool, instruction, region, size, source });
      if (!generating && !imageFileId(selected)) {
        attachmentRef.current.set(imageFileKey(selected), source);
      }
      editConversation.addRequest(target, { file: generating ? null : { ...source, id: imageFileId(source) }, prompt });
      setNotice('Added to chat. Review the request and press Send to run it with your selected AI.');
      setPreparedKey(requestKey);
      setPreparedPrompt(prompt);
    } catch (err) { if (aliveRef.current && operationRef.current === op) setError(err.message || 'Could not prepare the image request.'); }
    finally { if (aliveRef.current && operationRef.current === op) setBusy(false); }
  };

  const point = event => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)), y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)) };
  };
  const move = event => {
    if (!dragRef.current) return;
    const start = dragRef.current;
    const end = point(event);
    setRegion({ x: Math.min(start.x, end.x), y: Math.min(start.y, end.y), width: Math.abs(end.x - start.x), height: Math.abs(end.y - start.y) });
  };
  const fit = dimensions ? Math.min((viewport.width - 56) / dimensions.width, (viewport.height - 56) / dimensions.height, 1) : 1;
  const width = dimensions ? Math.max(1, dimensions.width * Math.max(0.01, fit) * zoom) : undefined;
  const canSelect = ['comment', 'remove', 'edit'].includes(tool) && !imageError && !!dimensions && !busy;

  return (
    <section className="image-studio" aria-label="Image studio" aria-busy={busy} onKeyDown={event => { if (event.key === 'Escape' && region && !busy) { event.stopPropagation(); setRegion(null); } }} style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, minWidth: 0, overflowY: 'auto', background: '#141414', color: '#eee', font: '13px/1.5 -apple-system, BlinkMacSystemFont, sans-serif', containerType: 'inline-size' }}>
      <style>{`
        .image-studio button:hover:not(:disabled) { filter: brightness(1.15); }
        .image-studio :focus-visible { outline: 2px solid #a8cdb2; outline-offset: 3px; }
        .image-studio button, .image-studio select { min-height: 36px; }
        .image-studio header, .image-studio footer, .image-studio-tools { flex-shrink: 0; }
        .image-studio-tools { scrollbar-width: thin; }
        .image-studio-tools button { white-space: nowrap; flex-shrink: 0; }
        @container (max-width: 540px) {
          .image-studio header, .image-studio footer { padding: 10px !important; }
          .image-studio-tools { justify-content: flex-start !important; padding: 8px 10px !important; }
          .image-studio-history { width: 66px !important; padding: 8px 6px !important; }
          .image-studio-submit { flex-wrap: wrap; }
        }
      `}</style>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid #303030' }}>
        <Icon name="image" size={17} color="#a8cdb2" />
        <div style={{ flex: 1, minWidth: 0 }}><strong>Image studio</strong><div style={{ color: '#a5a5a5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }}>{generating ? 'New image' : selected?.name || 'Create and edit with AI'}</div></div>
        <Button onClick={() => uploadRef.current?.click()} disabled={busy} title="Upload an image"><Icon name="arrow-up-from-bracket" size={13} color="#ddd" />Upload</Button>
        <Button onClick={async () => { try { const result = await downloadMedia(url, selected?.name || 'image'); if (result?.success === false) setError(result.message || 'Download failed. Please try again.'); } catch (err) { setError(err.message || 'Download failed.'); } }} disabled={generating || !url || imageError} aria-label="Download image" title="Download image"><Icon name="arrow-down-to-line" size={14} color="#ddd" /></Button>
        {onClose && <Button onClick={onClose} aria-label="Close image studio"><Icon name="xmark" size={14} color="#ddd" /></Button>}
        <input ref={uploadRef} type="file" accept="image/*" aria-label="Upload source image" onChange={upload} style={{ display: 'none' }} />
      </header>
      <div className="image-studio-tools" role="toolbar" aria-label="Image editing tools" style={{ padding: '10px 12px', display: 'flex', gap: 6, overflowX: 'auto', justifyContent: 'flex-start' }}>
        <Button aria-label="Generate a new image" active={generating} aria-pressed={generating} disabled={busy} onClick={() => changeTool('generate')}><Icon name="plus" size={13} color="#ddd" />New image</Button>
        {TOOLS.map(([id, icon, label]) => <Button key={id} active={tool === id} aria-pressed={tool === id} disabled={busy || !url || imageError} title={TOOL_HELP[id]} onClick={() => changeTool(id)}><Icon name={icon} size={13} color="#ddd" />{label}</Button>)}
      </div>
      <div style={{ display: 'flex', flex: 1, minHeight: 100 }}>
        <aside className="image-studio-history" aria-label="Task image history" style={{ width: 78, boxSizing: 'border-box', flexShrink: 0, overflowY: 'auto', padding: '8px 10px', borderRight: '1px solid #292929' }}>
          <div style={{ color: '#aaa', fontSize: 10, marginBottom: 10 }}>IMAGES</div>
          {allImages.map((item, index) => <button key={imageFileKey(item)} aria-label={`Open image ${index + 1}: ${item.name || 'Image'}`} title={item.name || 'Image'} aria-pressed={!generating && imageFileKey(item) === imageFileKey(selected)} onClick={() => select(item)} style={{ ...buttonStyle, width: 56, height: 60, padding: 3, marginBottom: 8, borderColor: !generating && imageFileKey(item) === imageFileKey(selected) ? '#a8cdb2' : '#383838' }}>
            {imageFileUrl(item) ? <img src={imageFileUrl(item)} alt={item.name || `Image ${index + 1}`} loading="lazy" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 5 }} /> : <span style={{ fontSize: 9, overflow: 'hidden', overflowWrap: 'anywhere', maxHeight: 48 }}>{item.name || `Image ${index + 1}`}</span>}
          </button>)}
        </aside>
        <div ref={canvasRef} style={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'auto' }}>
          <div style={{ minWidth: '100%', minHeight: '100%', width: 'max-content', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 28, boxSizing: 'border-box' }}>
            {generating ? <div style={{ textAlign: 'center', maxWidth: 320, color: '#aaa', padding: 12 }}>
              <Icon name="wand-magic-sparkles" size={30} color="#a8cdb2" />
              <h2 style={{ fontSize: 20, fontWeight: 600, color: '#eee', margin: '16px 0 6px' }}>Start with an idea</h2>
              <p style={{ margin: 0 }}>Describe your image below, then send it through chat. Your new image will appear here when it’s ready.</p>
            </div> : url && !imageError ? <div aria-label="Image canvas" style={{ position: 'relative', width, flexShrink: 0, lineHeight: 0, background: 'repeating-conic-gradient(#333 0% 25%, #292929 0% 50%) 0 / 20px 20px', boxShadow: '0 12px 48px #0005', cursor: canSelect ? 'crosshair' : 'default', touchAction: canSelect ? 'none' : 'auto' }}
              onPointerDown={event => { if (!canSelect || event.button !== 0) return; event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId); dragRef.current = point(event); setRegion(null); setNotice(''); }}
              onPointerMove={move}
              onPointerUp={event => { move(event); dragRef.current = null; setRegion(value => value && value.width > 0.005 && value.height > 0.005 ? value : null); }}
              onPointerCancel={() => { dragRef.current = null; setRegion(null); }}>
              <img ref={measureImage} src={url} alt={selected?.name || 'Source image'} draggable={false} onLoad={event => setImageDimensions({ url, width: event.target.naturalWidth, height: event.target.naturalHeight })} onError={() => setImageError(true)} style={{ display: 'block', width: dimensions ? '100%' : 'auto', maxWidth: dimensions ? undefined : '100%', pointerEvents: 'none' }} />
              {region && <div aria-label="Selected edit area" style={{ position: 'absolute', left: `${region.x * 100}%`, top: `${region.y * 100}%`, width: `${region.width * 100}%`, height: `${region.height * 100}%`, border: '2px solid #9fe5b5', background: '#9fe5b530', boxSizing: 'border-box', pointerEvents: 'none' }} />}
            </div> : <div role="status" style={{ color: '#aaa', maxWidth: 320, textAlign: 'center' }}>
              {imageError ? 'This image could not be loaded. Try another image or upload it again.'
                : loadErrors[imageFileKey(selected)] ? <>
                  <p>{loadErrors[imageFileKey(selected)]}</p>
                  <Button disabled={busy} onClick={() => select(selected)}>Retry loading image</Button>
                </> : imageFileKey(selected) ? 'Loading image…' : 'Upload an image to get started.'}
            </div>}
          </div>
        </div>
      </div>
      {!generating && <div style={{ display: 'flex', flexShrink: 0, flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '6px 16px', borderTop: '1px solid #292929', color: '#aaa', fontSize: 11 }}>
        <span style={{ flex: 1 }}>{dimensions ? `${dimensions.width} × ${dimensions.height}` : 'Image canvas'}{region ? ' · Area selected' : canSelect ? ' · Drag to select an area' : ''}</span>
        {region && <Button disabled={busy} onClick={() => setRegion(null)} style={{ padding: '4px 8px' }}>Clear selection</Button>}
        <label>Zoom <select aria-label="Canvas zoom" value={zoom} onChange={event => setZoom(Number(event.target.value))} style={{ ...buttonStyle, padding: 4 }}><option value={1}>Fit</option><option value={1.5}>150% of fit</option><option value={2}>200% of fit</option><option value={3}>300% of fit</option></select></label>
      </div>}
      <footer style={{ padding: '12px 16px 16px', borderTop: '1px solid #303030' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}><strong>{tool === 'generate' ? 'Generate a new image' : TOOLS.find(([id]) => id === tool)?.[2]} with AI</strong>{tool === 'resize' && <select aria-label="Image aspect ratio" disabled={busy} value={size} onChange={event => setSize(event.target.value)} style={{ ...buttonStyle, padding: 6 }}>{['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'].map(ratio => <option key={ratio}>{ratio}</option>)}</select>}</div>
        <p id="image-tool-help" style={{ color: '#aaa', fontSize: 12, margin: '0 0 8px' }}>{TOOL_HELP[tool]}</p>
        <textarea ref={instructionRef} aria-label="Image editing instructions" aria-describedby="image-tool-help" disabled={busy} value={instruction} onChange={event => { setInstruction(event.target.value); setNotice(''); }} placeholder={generating ? 'e.g. A sunlit cabin beside a mountain lake, watercolor…' : tool === 'remove' ? 'Optional details about the selected object…' : needsInstruction ? 'Describe what you want to change…' : 'Additional details (optional)…'} rows={2} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: 62, maxHeight: 120, padding: 12, border: '1px solid #404040', borderRadius: 12, background: '#242424', color: '#eee', font: 'inherit' }} />
        {error && <div role="alert" style={{ color: '#ffb6ac', marginTop: 8 }}>{error}</div>}
        {notice && alreadyPrepared && <div role="status" style={{ color: '#b4dfc0', marginTop: 8 }}>
          {notice}
          {onClose && <Button onClick={() => { onClose(); useChatInputStore.getState().requestInputFocus(useChatInputStore.getState().currentContextId); }} style={{ marginTop: 6, padding: '5px 9px' }}>Review in chat<Icon name="arrow-right" size={12} color="#b4dfc0" /></Button>}
        </div>}
        <div className="image-studio-submit" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 10 }}><span style={{ color: '#999', fontSize: 11 }}>Review in chat, then press Send. Originals stay available.</span><Button onClick={prepareEdit} disabled={busy || alreadyPrepared || !requestReady || (!generating && (!url || imageError))} style={{ background: '#e1f3e6', color: '#163921', borderColor: '#e1f3e6', flexShrink: 0 }}>{busy ? busyLabel : alreadyPrepared ? 'Added to chat' : 'Add to chat'}<Icon name={alreadyPrepared ? 'check' : 'arrow-right'} size={12} color="#163921" /></Button></div>
      </footer>
    </section>
  );
}
